import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Rule as RuleIcon,
  Assignment as FormIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { LOAN_APPLICATION_TEMPLATE } from '../../data/loanApplicationTemplate';
import { LOAN_PROCESSING_RULES, LOAN_WORKFLOW_TEMPLATES } from '../../data/loanRulesTemplates';

interface LoanWorkflowManagerProps {
  onCreateForm?: () => void;
  onCreateRule?: () => void;
  onEditForm?: (formId: string) => void;
  onEditRule?: (ruleId: string) => void;
  onCreateWorkflow?: () => void;
}

const LoanWorkflowManager: React.FC<LoanWorkflowManagerProps> = ({
  onCreateForm,
  onCreateRule,
  onCreateWorkflow
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [workflowRunning, setWorkflowRunning] = useState(false);
  const [previewDialog, setPreviewDialog] = useState<{ open: boolean; type: 'form' | 'rules' | null }>({
    open: false,
    type: null
  });

  const workflowTemplate = LOAN_WORKFLOW_TEMPLATES.STANDARD_LOAN_PROCESSING;

  const handleStartWorkflow = () => {
    setWorkflowRunning(true);
    setActiveStep(0);
    // In a real implementation, this would trigger the actual workflow
    console.log('Starting loan processing workflow');
  };

  const handleStopWorkflow = () => {
    setWorkflowRunning(false);
    // In a real implementation, this would stop the running workflow
    console.log('Stopping workflow');
  };

  const handleStepClick = (stepIndex: number) => {
    if (!workflowRunning) {
      setActiveStep(stepIndex);
    }
  };

  const handleDeployTemplate = async () => {
    try {
      // Deploy the pre-canned form
      console.log('Deploying loan application form template');
      
      // Deploy the pre-defined rules
      console.log('Deploying loan processing rules');
      
      // Set up the workflow
      console.log('Setting up workflow automation');
      
      alert('Loan processing workflow deployed successfully!');
    } catch (error) {
      console.error('Error deploying workflow:', error);
      alert('Error deploying workflow. Please try again.');
    }
  };

  const renderFormPreview = () => (
    <Dialog
      open={previewDialog.open && previewDialog.type === 'form'}
      onClose={() => setPreviewDialog({ open: false, type: null })}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormIcon />
          Loan Application Form Preview
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a preview of the pre-configured loan application form with {LOAN_APPLICATION_TEMPLATE.fields.length} fields.
        </Alert>
        
        <Typography variant="h6" gutterBottom>
          {LOAN_APPLICATION_TEMPLATE.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {LOAN_APPLICATION_TEMPLATE.description}
        </Typography>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Form Sections:
        </Typography>
        <List dense>
          {LOAN_APPLICATION_TEMPLATE.fields
            .filter(field => field.type === 'section')
            .map((section, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={section.label} />
              </ListItem>
            ))}
        </List>

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${LOAN_APPLICATION_TEMPLATE.fields.length} Fields`} size="small" />
          <Chip label={LOAN_APPLICATION_TEMPLATE.category} size="small" variant="outlined" />
          <Chip label="Auto-validation" size="small" color="success" />
          <Chip label="Document Upload" size="small" color="info" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewDialog({ open: false, type: null })}>
          Close
        </Button>
        <Button variant="contained" onClick={onCreateForm}>
          Use This Template
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRulesPreview = () => (
    <Dialog
      open={previewDialog.open && previewDialog.type === 'rules'}
      onClose={() => setPreviewDialog({ open: false, type: null })}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RuleIcon />
          Loan Processing Rules Preview
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {LOAN_PROCESSING_RULES.length} pre-configured rules for automated loan processing.
        </Alert>

        <Grid container spacing={2}>
          {LOAN_PROCESSING_RULES.map((rule, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ pb: 1.5 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {rule.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {rule.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    <Chip label={rule.category} size="small" />
                    <Chip label={`Priority ${rule.priority}`} size="small" variant="outlined" />
                    <Chip 
                      label={rule.isActive ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={rule.isActive ? 'success' : 'default'} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewDialog({ open: false, type: null })}>
          Close
        </Button>
        <Button variant="contained" onClick={onCreateRule}>
          Deploy These Rules
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Loan Application Workflow Manager
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage pre-configured loan application forms and automated processing rules
        </Typography>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onCreateWorkflow}
          >
            Create Custom Workflow
          </Button>
        </Box>
      </Box>

      {/* Quick Deploy Section */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
        <Typography variant="h5" gutterBottom>
          🚀 Quick Deploy: Complete Loan Processing Workflow
        </Typography>
        <Typography variant="body1" paragraph>
          Deploy a complete loan processing workflow with pre-configured application forms and automated rules.
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormIcon color="primary" />
                  <Typography variant="h6">Application Form</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Comprehensive 50-field loan application with validation, conditional logic, and document upload.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    startIcon={<ViewIcon />}
                    onClick={() => setPreviewDialog({ open: true, type: 'form' })}
                  >
                    Preview
                  </Button>
                  <Button size="small" startIcon={<EditIcon />} onClick={onCreateForm}>
                    Customize
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <RuleIcon color="primary" />
                  <Typography variant="h6">Processing Rules</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {LOAN_PROCESSING_RULES.length} automated rules for credit scoring, risk assessment, and approval workflows.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    startIcon={<ViewIcon />}
                    onClick={() => setPreviewDialog({ open: true, type: 'rules' })}
                  >
                    Preview
                  </Button>
                  <Button size="small" startIcon={<EditIcon />} onClick={onCreateRule}>
                    Customize
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ScheduleIcon color="primary" />
                  <Typography variant="h6">Automated Workflow</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  6-step workflow with automated decision making, task assignment, and notifications.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<ViewIcon />}>
                    Preview
                  </Button>
                  <Button size="small" startIcon={<EditIcon />}>
                    Customize
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            size="large" 
            startIcon={<PlayIcon />}
            onClick={handleDeployTemplate}
          >
            Deploy Complete Workflow
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            onClick={() => setPreviewDialog({ open: true, type: 'form' })}
          >
            Preview Components
          </Button>
        </Box>
      </Paper>

      {/* Workflow Steps */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            {workflowTemplate.name}
          </Typography>
          <Box>
            {workflowRunning ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopWorkflow}
              >
                Stop Workflow
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleStartWorkflow}
              >
                Start Workflow
              </Button>
            )}
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          {workflowTemplate.description}
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {workflowTemplate.steps.map((step, index) => (
            <Step key={step.name}>
              <StepLabel 
                onClick={() => handleStepClick(index)}
                sx={{ cursor: workflowRunning ? 'default' : 'pointer' }}
              >
                <Typography variant="h6">{step.name}</Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Automated Rules:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {step.rules.map((ruleName, ruleIndex) => (
                      <Chip
                        key={ruleIndex}
                        label={ruleName}
                        size="small"
                        icon={<RuleIcon />}
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  <Typography variant="subtitle1" gutterBottom>
                    Automated Actions:
                  </Typography>
                  <List dense>
                    {step.automatedActions.map((action, actionIndex) => (
                      <ListItem key={actionIndex} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={action.replace(/_/g, ' ')}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="subtitle1" gutterBottom>
                    Manual Tasks:
                  </Typography>
                  <List dense>
                    {step.manualTasks.map((task, taskIndex) => (
                      <ListItem key={taskIndex} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <WarningIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={task.replace(/_/g, ' ')}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Configuration Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Workflow Configuration
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom>
              Form Templates
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <FormIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Small Business Loan Application"
                  secondary="50 fields, validation, conditional logic"
                />
                <Box>
                  <Tooltip title="Preview Form">
                    <IconButton onClick={() => setPreviewDialog({ open: true, type: 'form' })}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Form">
                    <IconButton onClick={onCreateForm}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Create New Form Template"
                  secondary="Build a custom application form"
                />
                <IconButton onClick={onCreateForm}>
                  <AddIcon />
                </IconButton>
              </ListItem>
            </List>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" gutterBottom>
              Rule Templates
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <RuleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Loan Processing Rules"
                  secondary={`${LOAN_PROCESSING_RULES.length} automated rules`}
                />
                <Box>
                  <Tooltip title="Preview Rules">
                    <IconButton onClick={() => setPreviewDialog({ open: true, type: 'rules' })}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Rules">
                    <IconButton onClick={onCreateRule}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <AddIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Create New Rule"
                  secondary="Build custom business rules"
                />
                <IconButton onClick={onCreateRule}>
                  <AddIcon />
                </IconButton>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialogs */}
      {renderFormPreview()}
      {renderRulesPreview()}
    </Box>
  );
};

export default LoanWorkflowManager;
